import React, { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { Orb } from './Orb'
import { energyLineShaders } from './shaders'
import { OrbProvider } from './Orb'

// Add these constants at the top after imports
const MIN_DISTANCE = 3.5     // Slightly increased for better spacing
const SPREAD_FACTOR = 2.5    // Increased from 1.8 to spread orbs wider

// Add this helper function before orbData
const adjustPositionsForMinDistance = (orbDataArray) => {
  const adjustPosition = (position, otherPositions) => {
    let [x, y, z] = position
    let moved = false
    
    otherPositions.forEach(([ox, oy, oz]) => {
      const distance = Math.sqrt(
        Math.pow(ox - x, 2) +
        Math.pow(oy - y, 2) +
        Math.pow(oz - z, 2)
      )
      
      if (distance < MIN_DISTANCE) {
        // Push orbs apart if they're too close
        const angle = Math.atan2(y - oy, x - ox)
        const pushDistance = (MIN_DISTANCE - distance) * 0.5
        x += Math.cos(angle) * pushDistance
        y += Math.sin(angle) * pushDistance
        z += (z - oz) * 0.1 // Subtle z-axis adjustment
        moved = true
      }
    })
    
    return [[x, y, z], moved]
  }

  // First, spread out the initial positions
  const adjustedData = orbDataArray.map(orb => ({
    ...orb,
    position: orb.position.map(coord => coord * SPREAD_FACTOR)
  }))

  // Then, iteratively adjust positions to maintain minimum distance
  let iterations = 0
  let stillMoving = true
  const maxIterations = 50

  while (stillMoving && iterations < maxIterations) {
    stillMoving = false
    
    for (let i = 0; i < adjustedData.length; i++) {
      const otherPositions = adjustedData
        .filter((_, index) => index !== i)
        .map(o => o.position)
      
      const [newPosition, moved] = adjustPosition(adjustedData[i].position, otherPositions)
      if (moved) {
        adjustedData[i].position = newPosition
        stillMoving = true
      }
    }
    
    iterations++
  }

  return adjustedData
}

// Modify your orbData declaration to use the adjustment function
export const orbData = adjustPositionsForMinDistance([
  // Databases (Blue tones)
  {
    id: 1,
    title: "MySQL",
    description: "Relational database management system",
    details: "Industry-standard relational database. Commonly used with PHP for web applications and AWS RDS for cloud deployment.",
    color: "#4169E1",
    position: [-4, 2, 2],
    connections: [3, 15, 28, 27] // PHP, AWS RDS, PostgreSQL, Redis
  },
  {
    id: 2,
    title: "MongoDB",
    description: "NoSQL document database",
    details: "Modern NoSQL database optimized for JSON-like documents.",
    color: "#1E90FF",
    position: [-3, 3, -2],
    connections: [16, 13, 27, 20] // Node.js, React, Redis, Express.js
  },

  // Programming Languages (Green tones)
  {
    id: 3,
    title: "PHP",
    description: "Server-side scripting language",
    details: "Powerful server-side language for web development.",
    color: "#32CD32",
    position: [-2, 0, 1],
    connections: [1, 11, 29, 21] // MySQL, HTML, Authentication, REST APIs
  },
  {
    id: 4,
    title: "C++",
    description: "High-performance programming language",
    details: "Versatile language used for system programming.",
    color: "#228B22",
    position: [2, 3, -1],
    connections: [9, 24, 22] // Visual Studio, VS Code, Git
  },
  {
    id: 5,
    title: "Python",
    description: "Versatile programming language",
    details: "Multi-purpose language excellent for data analysis, web development, and automation.",
    color: "#3CB371",
    position: [0, 4, 1],
    connections: [7, 1, 8] // JupyterNotebook, MySQL, Docker
  },
  {
    id: 6,
    title: "Swift",
    description: "iOS app development language",
    details: "Apple's modern language for iOS development. Used with Xcode for creating iOS applications.",
    color: "#90EE90",
    position: [4, 2, 0],
    connections: [10, 24, 22, 29] // Added VS Code, Git, Authentication
  },

  // Development Tools (Purple tones)
  {
    id: 7,
    title: "JupyterNotebook",
    description: "Interactive computing platform",
    details: "Interactive development environment for data science, perfect for Python-based analysis and visualization.",
    color: "#9370DB",
    position: [1, 5, 0],
    connections: [5, 25, 24, 8] // Added Jest, VS Code, Docker
  },
  {
    id: 8,
    title: "Docker",
    description: "Container platform",
    details: "Containerization platform for consistent development and deployment. Works with multiple languages and cloud services.",
    color: "#8A2BE2",
    position: [-1, -3, 0],
    connections: [15, 14, 5, 23] // Added CI/CD
  },
  {
    id: 9,
    title: "Visual Studio",
    description: "Integrated development environment",
    details: "Powerful IDE for C++, C#, and other languages. Essential for Windows and cross-platform development.",
    color: "#9400D3",
    position: [3, 2, 1],
    connections: [4] // C++
  },
  {
    id: 10,
    title: "Android Studio",
    description: "Android development IDE",
    details: "Official IDE for Android app development. Used for creating mobile applications.",
    color: "#8B008B",
    position: [5, 1, 0],
    connections: [6] // Swift (mobile development connection)
  },

  // Web Technologies (Cyan tones)
  {
    id: 11,
    title: "HTML",
    description: "Web markup language",
    details: "Foundation of web development. Works with JavaScript and CSS for frontend development.",
    color: "#00CED1",
    position: [-1, 1, 0],
    connections: [12, 3] // JavaScript, PHP
  },
  {
    id: 12,
    title: "JavaScript",
    description: "Web programming language",
    details: "Essential language for web development. Powers React and Node.js applications.",
    color: "#20B2AA",
    position: [0, 2, 0],
    connections: [13, 16, 11] // React, Node.js, HTML
  },
  {
    id: 13,
    title: "React",
    description: "Frontend JavaScript library",
    details: "Modern library for building user interfaces. Often used with Node.js backend and MongoDB database.",
    color: "#48D1CC",
    position: [1, 1, 0],
    connections: [12, 16, 2] // JavaScript, Node.js, MongoDB
  },

  // Cloud/Infrastructure (Orange tones)
  {
    id: 14,
    title: "AWS EC2",
    description: "Cloud compute service",
    details: "Virtual servers in the cloud. Hosts web applications and services.",
    color: "#FFA500",
    position: [-2, -2, 0],
    connections: [15, 8] // AWS RDS, Docker
  },
  {
    id: 15,
    title: "AWS RDS",
    description: "Managed database service",
    details: "Cloud database service supporting MySQL and other databases. Simplifies database management.",
    color: "#FF8C00",
    position: [-3, -1, 0],
    connections: [1, 14, 8] // MySQL, AWS EC2, Docker
  },

  // Backend Runtime (Special color)
  {
    id: 16,
    title: "Node.js",
    description: "JavaScript runtime",
    details: "Server-side JavaScript runtime. Powers modern web applications with React frontend and MongoDB database.",
    color: "#66CC33",
    position: [0, 0, 0],
    connections: [13, 2, 12] // React, MongoDB, JavaScript
  },

  // Additional Frontend Technologies (Cyan tones)
  {
    id: 17,
    title: "TypeScript",
    description: "Typed superset of JavaScript",
    details: "Adds static typing to JavaScript. Essential for large-scale applications and improved developer experience.",
    color: "#007ACC",
    position: [1, 2, 1],
    connections: [12, 13, 16, 19] // Added Redux
  },
  {
    id: 18,
    title: "CSS Frameworks",
    description: "Tailwind CSS / Bootstrap",
    details: "Modern utility-first CSS framework (Tailwind) and component-based framework (Bootstrap) for rapid UI development.",
    color: "#38B2AC",
    position: [2, 1, 1],
    connections: [11, 13, 12, 24] // Added JavaScript, VS Code
  },
  {
    id: 19,
    title: "Redux",
    description: "State management library",
    details: "Predictable state container for JavaScript apps, commonly used with React for complex state management.",
    color: "#764ABC",
    position: [1, 3, 1],
    connections: [13, 17, 16, 20] // Added Node.js, Express.js
  },

  // Additional Backend Technologies (Green tones)
  {
    id: 20,
    title: "Express.js",
    description: "Node.js web framework",
    details: "Fast, unopinionated web framework for Node.js. Essential for building REST APIs and web applications.",
    color: "#259DFF",
    position: [-1, 2, 1],
    connections: [16, 21] // Node.js, REST APIs
  },
  {
    id: 21,
    title: "REST APIs",
    description: "API architectural style",
    details: "RESTful architecture for building scalable web services. Foundation of modern web development.",
    color: "#00FF9D",
    position: [-2, 1, 1],
    connections: [20, 16, 13] // Express.js, Node.js, React
  },

  // Development Tools (Purple tones)
  {
    id: 22,
    title: "Git",
    description: "Version control system",
    details: "Essential for code versioning and collaboration. Including GitHub/GitLab workflows and best practices.",
    color: "#F05032",
    position: [3, 1, -1],
    connections: [23, 24] // CI/CD, VS Code
  },
  {
    id: 23,
    title: "CI/CD",
    description: "Continuous Integration/Deployment",
    details: "Automated testing and deployment using GitHub Actions or GitLab CI. Essential for modern development workflows.",
    color: "#8B008B",
    position: [2, 2, -1],
    connections: [22, 8, 25, 24] // Added Jest, VS Code
  },
  {
    id: 24,
    title: "VS Code",
    description: "Code editor",
    details: "Popular code editor with extensive plugin ecosystem. Essential for modern web development.",
    color: "#007ACC",
    position: [1, 1, -1],
    connections: [22, 17] // Git, TypeScript
  },

  // Testing Tools (Blue tones)
  {
    id: 25,
    title: "Jest",
    description: "JavaScript testing framework",
    details: "Popular testing framework for JavaScript applications, especially React apps.",
    color: "#99425B",
    position: [0, 3, -1],
    connections: [13, 26] // React, Testing Library
  },
  {
    id: 26,
    title: "Testing Library",
    description: "Frontend testing utilities",
    details: "Suite of testing utilities focused on user behavior testing. Essential for React applications.",
    color: "#E33332",
    position: [-1, 2, -1],
    connections: [25, 13, 17, 19] // Added TypeScript, Redux
  },

  // Additional Databases/Storage (Blue tones)
  {
    id: 27,
    title: "Redis",
    description: "In-memory data store",
    details: "Fast in-memory database, commonly used for caching and real-time applications.",
    color: "#DC382D",
    position: [-3, 1, -1],
    connections: [1, 2] // MySQL, MongoDB
  },
  {
    id: 28,
    title: "PostgreSQL",
    description: "Advanced SQL database",
    details: "Powerful open-source relational database with advanced features.",
    color: "#336791",
    position: [-4, 0, -1],
    connections: [1, 15] // MySQL, AWS RDS
  },

  // Development Concepts (Orange tones)
  {
    id: 29,
    title: "Authentication",
    description: "User identity management",
    details: "JWT, OAuth, and session-based authentication. Essential for secure web applications.",
    color: "#FFA500",
    position: [-2, -1, -1],
    connections: [20, 16, 30, 21] // Added HTTP/HTTPS, REST APIs
  },
  {
    id: 30,
    title: "HTTP/HTTPS",
    description: "Web protocols",
    details: "Understanding of web protocols, status codes, and secure communication.",
    color: "#FF8C00",
    position: [-1, -2, -1],
    connections: [21, 29, 20, 16] // Added Express.js, Node.js
  }
])

export const OrbField = ({ onOrbClick }) => {
  const linesRef = useRef([])
  const lineGroupRef = useRef()
  const connectionPointsRef = useRef({})  // New ref to store connection points

  // Create shared uniforms for all lines
  const uniforms = useMemo(() => ({
    time: { value: 0 },
    color1: { value: new THREE.Color('#4fc3dc') },
    color2: { value: new THREE.Color('#ffffff') }
  }), [])

  // Enhanced animation frame updates
  useFrame(({ clock }) => {
    const time = clock.getElapsedTime()
    uniforms.time.value = time

    if (lineGroupRef.current) {
      lineGroupRef.current.rotation.y = Math.sin(time * 0.1) * 0.05
    }
  })

  const handleOrbClick = (data) => {
    // Highlight connected lines
    linesRef.current.forEach(line => {
      if (line && line.material && line.material.uniforms) {
        const [startId, endId] = line.userData.connection || []
        if (startId === data.id || endId === data.id) {
          line.material.uniforms.color1.value = new THREE.Color('#00ffff')
          line.material.uniforms.color2.value = new THREE.Color('#ffffff')
        } else {
          // Reset other lines to default colors
          line.material.uniforms.color1.value = new THREE.Color('#4fc3dc')
          line.material.uniforms.color2.value = new THREE.Color('#ffffff')
        }
      }
    })

    // Call the parent's click handler
    if (onOrbClick) {
      onOrbClick(data)
    }
  }

  // Create energy line material
  const createEnergyLineMaterial = (startColor, endColor) => {
    return new THREE.ShaderMaterial({
      uniforms: {
        time: uniforms.time,
        color1: { value: new THREE.Color(startColor) },
        color2: { value: new THREE.Color(endColor) }
      },
      vertexShader: energyLineShaders.vertexShader,
      fragmentShader: energyLineShaders.fragmentShader,
      transparent: true,
      depthWrite: false
    })
  }

  // Add this function to manage connection points
  const getConnectionPoint = (orbId, position) => {
    if (!connectionPointsRef.current[orbId]) {
      connectionPointsRef.current[orbId] = new THREE.Vector3(...position)
    }
    return connectionPointsRef.current[orbId]
  }

  return (
    <OrbProvider>
      <group ref={lineGroupRef}>
        {/* Render Orbs */}
        {orbData.map((data) => (
          <Orb
            key={data.id}
            position={data.position}
            data={data}
            onOrbClick={handleOrbClick}
          />
        ))}
        
        {/* Enhanced Energy Connection Lines */}
        {orbData.map(orb => 
          orb.connections.map(connectionId => {
            const connectedOrb = orbData.find(o => o.id === connectionId)
            if (connectedOrb) {
              // Get stable connection points
              const startPoint = getConnectionPoint(orb.id, orb.position)
              const endPoint = getConnectionPoint(connectionId, connectedOrb.position)
              
              // Calculate midpoint
              const midPoint = new THREE.Vector3(
                (startPoint.x + endPoint.x) / 2,
                (startPoint.y + endPoint.y) / 2 + 0.5,
                (startPoint.z + endPoint.z) / 2
              )

              const curve = new THREE.QuadraticBezierCurve3(
                startPoint,
                midPoint,
                endPoint
              )

              const tubeGeometry = new THREE.TubeGeometry(
                curve,
                64,    // tubular segments
                0.045, // radius
                12,    // radial segments
                false  // closed
              )

              return (
                <mesh
                  key={`${orb.id}-${connectionId}`}
                  geometry={tubeGeometry}
                  ref={el => {
                    if (el) {
                      el.userData.connection = [orb.id, connectionId]
                      linesRef.current.push(el)
                    }
                  }}
                >
                  <shaderMaterial
                    attach="material"
                    args={[createEnergyLineMaterial(orb.color, connectedOrb.color)]}
                    transparent
                    depthWrite={false}
                  />
                </mesh>
              )
            }
            return null
          })
        )}
      </group>
    </OrbProvider>
  )
} 